import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useEditor, EditorContent, FloatingMenu } from '@tiptap/react';
import type { Instance as TippyInstance } from 'tippy.js';
import { getRichTextEditorExtensions } from './RichTextEditorExtensions';
import MenuBar from './MenuBar';
import LinkMenu from './LinkMenu';
import { TaskAttachment } from '../../../../types/TaskAttachment';
import { CommentAttachment } from '../../../../types/CommentAttachment';
import { TaskLink } from '../../../../types/TaskLink';
import { TaskLinkModal } from '../../../../components/Modal/TaskLinkModal';
import { BaseButton } from '../../../../components/UI/BaseButton';
import './styles/RichTextEditor.scss';
import { normalizeUrlForOpening } from '../../../../utils/urlUtils';
import { useGetUserFromAccessToken } from '../../../../common/auth';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { applyExternalContent } from './applyExternalContent';

export interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  onBlur?: () => void;
  onAddSelectionToContext?: (selectedText: string) => void;
  usersWithAccessToSpace?: Array<{
    id: number;
    fullName?: string | null;
    email: string;
    profilePicturePath?: string | null;
  }>;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  showSubmitButton?: boolean;
  submitButtonText?: string;
  showCancelButton?: boolean;
  cancelButtonText?: string;
  onCancel?: () => void;
  onImageUpload?: (file: File) => Promise<TaskAttachment | null>;
  onCommentAttachmentUpload?: (files: File[]) => Promise<CommentAttachment[]>;
  onCopyTaskDescriptionLink?: () => void;
  minHeight?: string;
  mode?: 'description' | 'comment';
  middleContent?: React.ReactNode;
  autoFocus?: boolean;
  hideImageUpload?: boolean;
  reserveToolbarSpace?: boolean;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = React.memo(
  ({
    value,
    onChange,
    onSubmit,
    onBlur,
    onAddSelectionToContext,
    usersWithAccessToSpace = [],
    placeholder = 'Start writing...',
    disabled = false,
    readOnly = false,
    showSubmitButton = false,
    submitButtonText = 'Submit',
    showCancelButton = false,
    cancelButtonText = 'Cancel',
    onImageUpload,
    onCommentAttachmentUpload,
    onCancel,
    onCopyTaskDescriptionLink = () => {},
    minHeight = '200px',
    mode = 'description',
    middleContent,
    autoFocus = false,
    hideImageUpload = false,
    reserveToolbarSpace = false,
  }) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isFileSelecting, setIsFileSelecting] = useState(false);
    const [openTaskLinkModal, setOpenTaskLinkModal] = useState(false);
    const [linkElement, setLinkElement] = useState<HTMLElement | null>(null);
    const [taskLink, setTaskLink] = useState<TaskLink | null>(null);
    const [showMenu, setShowMenu] = useState(false);
    const [showLinkMenu, setShowLinkMenu] = useState(false);
    const [isAttachmentInteracting, setIsAttachmentInteracting] = useState(false);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const floatingMenuTippyRef = useRef<TippyInstance | null>(null);

    // Get current user for mention color differentiation
    const currentUserQuery = useGetUserFromAccessToken();
    const currentUser = currentUserQuery.data?.user;

    // Transform users data for mention functionality
    const mentionUsers = useMemo(() => {
      return usersWithAccessToSpace.map((user) => ({
        id: user.id,
        display: user.fullName || user.email,
        profilePicturePath: user.profilePicturePath || undefined,
      }));
    }, [usersWithAccessToSpace]);

    const editor = useEditor({
      extensions: getRichTextEditorExtensions(
        mentionUsers,
        mode === 'comment',
        currentUser ? { id: currentUser.id, email: currentUser.email, fullName: currentUser.fullName } : undefined
      ),
      content: value || '',
      editable: !disabled && !readOnly,
      onUpdate: ({ editor }) => {
        // Mark that user has interacted when they're actively editing
        if (isFocused) {
          setHasUserInteracted(true);
        }

        const html = editor.getHTML();
        const hasListsOrStructures =
          html.includes('<ul>') ||
          html.includes('<ol>') ||
          html.includes('<li>') ||
          html.includes('<task-list>') ||
          html.includes('<task-item>');

        // Transform mentions from Tiptap format to the expected format for comments
        let processedHtml = html;
        if (mode === 'comment') {
          processedHtml = html.replace(
            /<span[^>]*data-type="mention"[^>]*data-id="(\d+)"[^>]*>@([^<]+)<\/span>/g,
            '@[$2]($1)'
          );
        }

        // Only return empty string if truly empty (not just a structure like a list)
        onChange?.(editor.isEmpty && !hasListsOrStructures ? '' : processedHtml);
      },
      onSelectionUpdate: ({ editor }) => {
        // Only auto-scroll if the user has interacted with the editor
        // This prevents unwanted scrolling during initial page load
        if (!hasUserInteracted) {
          return;
        }

        // Auto-scroll to cursor position when selection changes (e.g., after ENTER)
        const { view } = editor;
        const { from } = view.state.selection;

        // Use requestAnimationFrame to ensure DOM is updated before scrolling
        requestAnimationFrame(() => {
          try {
            // Get the DOM position of the cursor
            const coords = view.coordsAtPos(from);

            // Find the scrollable container - could be the editor itself or a parent
            let scrollContainer: HTMLElement | null = view.dom;

            // Look for the nearest scrollable parent
            while (scrollContainer && scrollContainer !== document.body) {
              const style = window.getComputedStyle(scrollContainer);
              if (
                style.overflow === 'auto' ||
                style.overflow === 'scroll' ||
                style.overflowY === 'auto' ||
                style.overflowY === 'scroll'
              ) {
                break;
              }
              scrollContainer = scrollContainer.parentElement;
            }

            if (scrollContainer && coords) {
              const containerRect = scrollContainer.getBoundingClientRect();
              const cursorY = coords.top;
              const cursorHeight = coords.bottom - coords.top;

              // Account for the floating bottom MenuBar height when focused
              let bottomOffset = 0;
              if (isFocused) {
                // The MenuBar is sticky and takes up space at the bottom
                // Look for the MenuBar element to get its actual height
                const menuBar = scrollContainer.querySelector('.sticky.bottom-0');
                if (menuBar) {
                  bottomOffset = menuBar.getBoundingClientRect().height;
                }
              }

              // Check if cursor is visible within the container (accounting for MenuBar)
              const isAboveContainer = cursorY < containerRect.top;
              const isBelowContainer = coords.bottom > containerRect.bottom - bottomOffset;

              if (isAboveContainer || isBelowContainer) {
                // Calculate the scroll position to bring cursor into view
                let targetScrollTop = scrollContainer.scrollTop;

                if (isAboveContainer) {
                  // Scroll up to show cursor
                  targetScrollTop += cursorY - containerRect.top - 20; // 20px margin
                } else if (isBelowContainer) {
                  // Scroll down to show cursor, accounting for MenuBar and adding margin
                  targetScrollTop += coords.bottom - (containerRect.bottom - bottomOffset) + 20; // 20px margin
                }

                scrollContainer.scrollTo({
                  top: targetScrollTop,
                  behavior: 'smooth',
                });
              }
            }
          } catch (error) {
            // Fallback: simple scroll into view
            try {
              const cursorElement = view.domAtPos(from).node;
              if (cursorElement && cursorElement.nodeType === Node.ELEMENT_NODE) {
                (cursorElement as Element).scrollIntoView({
                  behavior: 'smooth',
                  block: 'nearest',
                });
              }
            } catch (fallbackError) {
              console.warn('Auto-scroll failed:', fallbackError);
            }
          }
        });
      },
      onFocus: () => {
        // Cancel any pending blur-close: focus has returned (e.g. after clicking
        // a mention suggestion, which momentarily blurs the editor), so a composer
        // that closes on blur (the reply composer) must not discard the draft.
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
          blurTimeoutRef.current = null;
        }
        setIsFocused(true);
        setHasUserInteracted(true);
      },
      onBlur: (event) => {
        if (openTaskLinkModal || isFileSelecting || isAttachmentInteracting) {
          return;
        }

        // Delay blur handling to allow button clicks to complete, but only if submit button is present
        if (showSubmitButton) {
          blurTimeoutRef.current = setTimeout(() => {
            setIsFocused(false);
            onBlur?.();
          }, 150);
        } else {
          setIsFocused(false);
          onBlur?.();
        }
      },
    });

    // Set the image upload function in the editor storage for the ImagePaste extension
    useEffect(() => {
      if (editor && onImageUpload) {
        editor.storage.imagePaste.onImageUpload = onImageUpload;
      }
    }, [editor, onImageUpload]);

    // Update editor editable state when disabled or readOnly changes
    useEffect(() => {
      if (editor) {
        editor.setEditable(!disabled && !readOnly);
      }
    }, [editor, disabled, readOnly]);

    // Update editor content when value changes externally
    useEffect(() => {
      if (editor && value !== undefined && !isFocused) {
        let processedValue = value;

        // Transform mentions from @[DisplayName](userId) format to Tiptap format for comments
        if (mode === 'comment') {
          processedValue = value.replace(
            /@\[([^\]]+)\]\((\d+)\)/g,
            '<span data-type="mention" data-id="$2" class="mention">@$1</span>'
          );
        }

        // Not a plain setContent: that would put the load itself on the undo
        // stack, so the first Cmd/Ctrl+Z after opening a task would undo the
        // load and leave the description empty (#3140).
        applyExternalContent(editor, processedValue);
      }
    }, [value, editor, isFocused, mode]);

    // Handle keyboard shortcuts for comments (#637).
    // The Cmd/Ctrl+Enter submit is bound inside the editor's keymap (the
    // CommentKeyboardShortcuts extension) so it reliably wins over the default
    // Enter behaviour; here we just wire the submit callback into editor storage.
    useEffect(() => {
      if (!editor || mode !== 'comment') return;

      const commentStorage = editor.storage.commentKeyboardShortcuts as { onSubmit: (() => void) | null } | undefined;
      if (!commentStorage) return;

      commentStorage.onSubmit = onSubmit
        ? () => {
            onSubmit();
            // Blur the editor after submit
            editor.commands.blur();
          }
        : null;

      return () => {
        commentStorage.onSubmit = null;
      };
    }, [editor, onSubmit, mode]);

    // Handle link clicks and menu positioning
    useEffect(() => {
      if (!editor) return;

      const handleClick = (event: Event) => {
        const clickedElement = event.target as HTMLElement;
        const linkElement = clickedElement.closest('a');

        if (linkElement) {
          event.preventDefault();
          event.stopPropagation();
          const href = linkElement.getAttribute('href');
          if (href) {
            if (disabled) {
              const urlToOpen = normalizeUrlForOpening(href);
              window.open(urlToOpen, '_blank');
              return;
            }
            setLinkElement(linkElement);
            setTaskLink(taskLinkFrom(linkElement));
            setShowLinkMenu(true);
          }
        }
      };

      const editorElement = editor.view.dom;
      if (editorElement) {
        editorElement.addEventListener('click', handleClick);
      }

      return () => {
        if (editorElement) {
          editorElement.removeEventListener('click', handleClick);
        }
      };
    }, [editor]);

    // Handle floating menu visibility
    useEffect(() => {
      // `invisible` only hides the menu's content. Its tippy box stays mounted above the
      // selection and, being interactive, takes the pointer, so a drag moving up into the
      // line under it could not extend the selection there (#3256). Turning `interactive`
      // off while the button is down makes tippy set `pointer-events: none` on the box.
      // setProps rebuilds the popper, and every mounted editor runs these handlers on every
      // click, so it is only called when the value actually changes.
      const setMenuInteractive = (interactive: boolean) => {
        const instance = floatingMenuTippyRef.current;
        if (instance && instance.props.interactive !== interactive) {
          instance.setProps({ interactive });
        }
      };
      const handleMouseDown = () => {
        setShowMenu(false);
        setMenuInteractive(false);
      };
      const handleMouseUp = () => {
        setMenuInteractive(true);
        setTimeout(() => setShowMenu(true), 50);
      };

      document.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousedown', handleMouseDown);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }, []);

    // Handle link menu click outside
    useEffect(() => {
      if (!showLinkMenu) return;

      const handleClickOutside = (event: MouseEvent) => {
        if (linkElement && linkElement.contains(event.target as Node)) return;

        const linkMenuElement = document.querySelector('.link-menu-container');
        if (linkMenuElement && linkMenuElement.contains(event.target as Node)) return;

        setShowLinkMenu(false);
      };

      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside);
      };
    }, [showLinkMenu, linkElement]);

    // Cleanup blur timeout on unmount
    useEffect(() => {
      return () => {
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
        }
      };
    }, []);

    // Handle autoFocus
    useEffect(() => {
      if (autoFocus && editor && !isFocused) {
        // Small delay to ensure editor is fully mounted
        const timer = setTimeout(() => {
          // Focus at the end of the document
          editor.commands.focus('end');
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [autoFocus, editor, isFocused]);

    // Handle attachment interaction events to prevent blur
    useEffect(() => {
      const handleAttachmentMouseDown = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        // Check if the click is on an attachment or its children
        if (
          target.closest('.cursor-pointer.relative.group') ||
          target.closest('[class*="ant-modal"]') ||
          target.closest('[class*="ant-dropdown"]') ||
          target.closest('[role="tooltip"]')
        ) {
          setIsAttachmentInteracting(true);
        }
      };

      const handleAttachmentMouseUp = () => {
        // Reset attachment interaction state after a delay
        setTimeout(() => {
          setIsAttachmentInteracting(false);
        }, 200);
      };

      document.addEventListener('mousedown', handleAttachmentMouseDown);
      document.addEventListener('mouseup', handleAttachmentMouseUp);

      return () => {
        document.removeEventListener('mousedown', handleAttachmentMouseDown);
        document.removeEventListener('mouseup', handleAttachmentMouseUp);
      };
    }, []);

    // Handle click outside to blur editor
    useEffect(() => {
      if (!editor || !isFocused) return;

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const editorElement = editor.view.dom;

        // Find the editor's wrapper (the parent with border and styling)
        const editorWrapper = editorElement.closest('.box-border.flex.flex-col.border');

        // Check if click is outside the editor wrapper
        if (
          editorWrapper &&
          !editorWrapper.contains(target) &&
          !target.closest('[class*="ant-dropdown"]') &&
          !target.closest('[role="tooltip"]') &&
          !target.closest('.link-menu-container')
        ) {
          editor.commands.blur();
          setIsFocused(false);
        }
      };

      // Add listener with a small delay to avoid immediate triggers
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [editor, isFocused]);

    // Helper function to check if content is only whitespace
    const isContentOnlyWhitespace = useCallback(() => {
      if (!editor) return false;
      const textContent = editor.getText();
      return textContent.trim() === '' && textContent.length > 0;
    }, [editor]);

    // Helper function to check if content has valid characters
    const hasValidContent = useCallback(() => {
      if (!editor) return false;
      const textContent = editor.getText();
      return textContent.trim().length > 0;
    }, [editor]);

    const handleSubmitClick = useCallback(() => {
      // Cancel any pending blur timeout
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      // Only submit if there's valid content (not empty and not just whitespace)
      if (hasValidContent()) {
        onSubmit?.();

        // Blur the editor after submit
        if (editor) {
          editor.commands.blur();
        }
      }
      // If empty or only whitespace, do nothing (button click has no effect)
    }, [onSubmit, editor, hasValidContent]);

    const createLink = (url: string, name: string) => addOrUpdateLink(url, name);
    const updateLink = (taskLinkId: number, url: string, name: string) => addOrUpdateLink(url, name);

    const addOrUpdateLink = (url: string, name: string) => {
      setOpenTaskLinkModal(false);

      if (!editor) return;

      editor.commands.focus();

      if (linkElement) {
        try {
          const positions = findNodePositionInEditor(editor, linkElement);
          if (positions) {
            editor.commands.setTextSelection({ from: positions.from, to: positions.to });

            if (name && name !== linkElement.textContent) {
              editor.chain().unsetLink().run();
              editor.chain().insertContent(name).run();
              const currentPos = editor.state.selection.$from.pos;
              const newFrom = currentPos - name.length;
              editor.commands.setTextSelection({ from: newFrom, to: currentPos });
              editor.commands.setLink({ href: url });
            } else {
              editor.commands.setLink({ href: url });
            }
          } else {
            const displayText = name || url;
            editor.commands.insertContent(`<a href="${url}">${displayText}</a>`);
          }

          setTimeout(() => {
            if (editor) {
              const currentPos = editor.state.selection.$to.pos;
              editor.commands.setTextSelection({ from: currentPos, to: currentPos });
            }
          }, 0);
        } catch (e) {
          console.error('Error updating link:', e);
          const displayText = name || url;
          editor.commands.insertContent(`<a href="${url}">${displayText}</a>`);
        }
      } else {
        const { from, to } = editor.state.selection;
        const hasSelection = from !== to;

        if (hasSelection) {
          editor.commands.setLink({ href: url });
        } else {
          const displayText = name || url;
          editor.commands.insertContent(`<a href="${url}">${displayText}</a>`);
        }
      }

      setTaskLink(null);
      setLinkElement(null);
    };

    const findNodePositionInEditor = (editor: any, domNode: HTMLElement) => {
      try {
        const { view } = editor;
        try {
          const pos = view.posAtDOM(domNode, 0);
          if (typeof pos === 'number') {
            const $pos = view.state.doc.resolve(pos);
            const node = $pos.nodeAfter;
            if (node) {
              return { from: pos, to: pos + node.nodeSize };
            }
            return { from: pos, to: pos + domNode.textContent!.length };
          }
        } catch (e) {
          console.log('Error getting position from DOM node:', e);
        }

        const links: any[] = [];
        view.state.doc.descendants((node: any, pos: number) => {
          if (node.type.name === 'text' && node.marks.some((mark: any) => mark.type.name === 'link')) {
            links.push({ node, pos, end: pos + node.nodeSize });
          }
          return true;
        });

        const targetText = domNode.textContent || '';
        const match = links.find((link) => link.node.text === targetText);

        if (match) {
          return { from: match.pos, to: match.end };
        }

        return null;
      } catch (e) {
        console.error('Error in findNodePositionInEditor:', e);
        return null;
      }
    };

    const taskLinkFrom = (element: HTMLElement) => {
      return {
        id: 1,
        link: element.getAttribute('href') || '',
        name: element.getAttribute('href') === element.textContent ? '' : element.textContent || '',
        taskId: 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    };

    const taskLinkFromSelection = () => {
      if (!editor) return null;
      const { from, to } = editor.state.selection;
      const selectedText = editor.state.doc.textBetween(from, to, ' ');

      return {
        id: 1,
        link: '',
        name: selectedText,
        taskId: 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    };

    const onAddLink = () => {
      setTaskLink(null);
      setLinkElement(null);

      if (editor) {
        const { view } = editor;
        const { from, to } = view.state.selection;
        const domElement = view.domAtPos(from).node as HTMLElement;

        if (domElement.textContent?.length === from - to) {
          setLinkElement(domElement);
          setTaskLink(taskLinkFrom(domElement));
        } else {
          setTaskLink(taskLinkFromSelection());
        }
      }

      setOpenTaskLinkModal(true);
    };

    if (!editor) {
      return null;
    }

    const shouldShowCTA = !isFocused && !disabled && mode === 'comment' && hasValidContent();

    const placeholderClassName =
      '[&_.is-editor-empty]:before:content-[attr(data-placeholder)] [&_.is-editor-empty]:before:float-left [&_.is-editor-empty]:before:text-neutral-700 [&_.is-editor-empty]:before:pointer-events-none [&_.is-editor-empty]:before:h-0';

    return (
      <div className="w-full">
        <div
          className={`
          box-border flex flex-col border border-solid rounded transition-colors bg-transparent
          ${isFocused ? 'border-taskonioGreen-500 outline-taskonioGreen-300 outline-1' : disabled ? 'border-transparent' : 'border-neutral-300'}
          ${placeholderClassName}
          ${!readOnly && !disabled ? 'cursor-text' : ''}
        `}
          style={{ minHeight }}
        >
          {/* Editor Content */}
          <div
            className={`flex-1 ${disabled ? 'p-0' : mode === 'comment' ? 'px-4 pt-2' : 'px-4 pt-4'} ${readOnly ? 'cursor-default' : 'cursor-text'}`}
            onClick={(e) => {
              if (readOnly || disabled) return;
              const proseMirrorEl = document.querySelector('.ProseMirror');
              if (proseMirrorEl && !proseMirrorEl.contains(e.target as Node) && editor) {
                editor.commands.focus();
              }
            }}
          >
            <EditorContent
              editor={editor}
              className={`w-full font-figtree text-base outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:focus:outline-none [&_.ProseMirror]:focus-visible:outline-none [&_.ProseMirror]:focus-within:outline-none [&_.ProseMirror]:p-0 [&_.comment-link]:text-blue-600 [&_.comment-link]:underline ${(reserveToolbarSpace || !isFocused) && !disabled && !middleContent ? 'pb-[56px]' : 'pb-0'}`}
              style={{ minHeight: mode === 'comment' ? '0px' : '160px' }}
            />

            {/* Link Menu */}
            {editor &&
              showLinkMenu &&
              linkElement &&
              ReactDOM.createPortal(
                <div
                  className="inline-flex bg-white rounded-md shadow border border-neutral-200 absolute z-50 link-menu-container"
                  style={{
                    left: linkElement.getBoundingClientRect().left,
                    top: linkElement.getBoundingClientRect().top - 48,
                  }}
                >
                  <LinkMenu
                    editor={editor}
                    onOpenLink={() => {
                      if (linkElement) {
                        const href = linkElement.getAttribute('href');
                        if (href) {
                          const urlToOpen = normalizeUrlForOpening(href);
                          window.open(urlToOpen, '_blank');
                        }
                      }
                      setShowLinkMenu(false);
                    }}
                    onEditLink={() => {
                      setOpenTaskLinkModal(true);
                      setShowLinkMenu(false);
                    }}
                    onCopyLink={() => {
                      if (linkElement) {
                        const href = linkElement.getAttribute('href');
                        if (href) {
                          navigator.clipboard.writeText(href);
                          onCopyTaskDescriptionLink();
                        }
                      }
                      setShowLinkMenu(false);
                    }}
                    onDeleteLink={() => {
                      if (editor) {
                        editor.commands.unsetLink();
                      }
                      setShowLinkMenu(false);
                    }}
                  />
                </div>,
                document.body
              )}

            {/* Floating Menu for Text Selection */}
            {editor && (
              <FloatingMenu
                editor={editor}
                shouldShow={({ editor }) => {
                  if (disabled) return false;
                  const selection = editor.state.selection;
                  const selectedText = editor.view.state.doc.textBetween(selection.from, selection.to, ' ');
                  return selectedText.trim().length > 0;
                }}
                tippyOptions={{
                  onCreate: (instance) => {
                    floatingMenuTippyRef.current = instance;
                  },
                  onDestroy: (instance) => {
                    if (floatingMenuTippyRef.current === instance) floatingMenuTippyRef.current = null;
                  },
                  // Tippy mounts an interactive tippy next to its reference and any other in
                  // document.body. Pinning it keeps the menu in one place while the mousedown
                  // handler above switches `interactive` off.
                  appendTo: 'parent',
                  duration: 100,
                  placement: 'top',
                  offset: [0, 8],
                  zIndex: 30000,
                  maxWidth: 'none',
                  popperOptions: {
                    strategy: 'fixed',
                    modifiers: [
                      {
                        name: 'preventOverflow',
                        options: {
                          boundary: 'viewport',
                          padding: 16,
                          altAxis: true,
                          tether: false,
                        },
                      },
                    ],
                  },
                }}
              >
                <div
                  className={`inline-flex p-1 bg-white rounded-md shadow border border-neutral-200 ${showMenu ? 'visible' : 'invisible'}`}
                >
                  <MenuBar
                    editor={editor}
                    onImageUpload={onImageUpload || (async () => null)}
                    onCommentAttachmentUpload={onCommentAttachmentUpload || (async () => [])}
                    onAddLink={onAddLink}
                    isFloating={true}
                    setIsFileSelecting={setIsFileSelecting}
                    mode={mode}
                    hideImageUpload={hideImageUpload}
                    onAddSelectionToContext={onAddSelectionToContext}
                  />
                </div>
              </FloatingMenu>
            )}
          </div>

          {middleContent && (
            <div
              className={`${!isFocused && !disabled && !shouldShowCTA ? 'pb-[56px]' : 'pb-0'}`}
              onClick={() => {
                setIsFocused(true);
                editor.commands.focus();
              }}
            >
              {middleContent}
            </div>
          )}

          {/* Bottom MenuBar - Always visible when focused */}
          {isFocused && (
            <div className="sticky bottom-0 left-0 right-0 z-10 bg-white border-t border-neutral-200 flex items-center">
              <div className="flex-1">
                <MenuBar
                  editor={editor}
                  onImageUpload={onImageUpload || (async () => null)}
                  onCommentAttachmentUpload={onCommentAttachmentUpload || (async () => [])}
                  onAddLink={onAddLink}
                  isFloating={false}
                  setIsFileSelecting={setIsFileSelecting}
                  mode={mode}
                  hideImageUpload={hideImageUpload}
                />
              </div>
              {/* Inline Comment Button - Only shown for comments and when focused */}
              {showSubmitButton && (
                <div
                  className="flex items-center gap-2 pr-2"
                  onMouseDown={(e) => {
                    // Prevent editor blur when clicking in the submit button area
                    e.preventDefault();
                  }}
                >
                  {showCancelButton && (
                    <BaseButton
                      type="secondary"
                      size="small"
                      onClick={(e: React.MouseEvent<HTMLElement>) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onCancel?.();
                      }}
                    >
                      {cancelButtonText}
                      {cancelButtonText.length == 0 && <CloseOutlined />}
                    </BaseButton>
                  )}
                  <BaseButton
                    type="accent"
                    size="small"
                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSubmitClick();
                    }}
                    disabled={disabled || isContentOnlyWhitespace()}
                  >
                    {submitButtonText}
                    {submitButtonText.length === 0 && <CheckOutlined />}
                  </BaseButton>
                </div>
              )}
            </div>
          )}
          {shouldShowCTA && (
            <div
              className="w-full h-[56px] flex items-center justify-end gap-2 pr-2"
              onClick={() => {
                setIsFocused(true);
                editor.commands.focus();
              }}
            >
              <BaseButton
                type="accent"
                size="small"
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSubmitClick();
                }}
                disabled={disabled || isContentOnlyWhitespace()}
              >
                {submitButtonText}
              </BaseButton>
            </div>
          )}
        </div>

        <TaskLinkModal
          open={openTaskLinkModal}
          onAdd={createLink}
          onEdit={updateLink}
          onCancel={() => {
            setOpenTaskLinkModal(false);
            setTaskLink(null);
            setLinkElement(null);
          }}
          confirmLoading={false}
          taskLink={taskLink || undefined}
        />
      </div>
    );
  }
);

RichTextEditor.displayName = 'RichTextEditor';
